// packages/shared/src/schemas/quote.ts
import { z } from 'zod'
import { optionalEmail } from './optional-email.js'

export const QuoteStatusEnum = z.enum([
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'converted',
])

export const QuoteItemInput = z.object({
  productId: z.string(),
  variantId: z.string().optional(),
  productName: z.string(),
  variantDescription: z.string().optional(),
  unitPrice: z.number().min(0),
  quantity: z.number().int().min(1),
  discountPct: z.number().min(0).max(100).default(0),
  sortOrder: z.number().int().default(0),
})

export const QuoteItemSchema = QuoteItemInput.extend({
  id: z.string(),
  quoteId: z.string(),
  lineTotal: z.number().min(0),
  createdAt: z.string(),
})

export const QuoteSchema = z.object({
  id: z.string(),
  quoteNumber: z.string(),
  customerPhone: z.string(),
  customerName: z.string(),
  customerEmail: z.string().nullable(),
  status: QuoteStatusEnum,
  subtotal: z.number().min(0),
  discountAmount: z.number().min(0),
  taxCgst: z.number().min(0),
  taxSgst: z.number().min(0),
  total: z.number().min(0),
  validUntil: z.string().nullable(),
  notes: z.string().nullable(),
  convertedOrderId: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  items: z.array(QuoteItemSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const CreateQuoteInput = z.object({
  customerPhone: z.string().regex(/^[6-9]\d{9}$/, 'Valid 10-digit Indian mobile required'),
  customerName: z.string().min(1).max(100).trim(),
  customerEmail: optionalEmail,
  notes: z.string().max(500).trim().optional(),
  items: z.array(QuoteItemInput).min(1, 'Add at least one item'),
})

export const UpdateQuoteInput = CreateQuoteInput.partial()

export const SendQuoteInput = z.object({
  validDays: z.number().int().min(1).max(30).default(7),
})

export const QuoteListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  status: QuoteStatusEnum.optional(),
  search: z.string().trim().optional(),
  sortBy: z.enum(['created_at', 'total', 'quote_number']).default('created_at'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
})

export type Quote = z.infer<typeof QuoteSchema>
export type CreateQuoteInput = z.infer<typeof CreateQuoteInput>
